import { sortBy } from "lodash-es";

import {
  createAppSelector,
  selectCourseOutcomes,
  selectOutcomeGroups,
  selectOutcomeSubgroups,
  selectOutcomes,
  selectPageOutcomes,
} from "./base";
import { selectCurrentOrg } from "./org";
import { selectCoursesForCurrentUser } from "./section";
import {
  NestedOutcomeGroupType,
  NestedOutcomeSubgroupType,
  NestedPageOutcomeType,
  NestedSectionOutcomeType,
} from "./types";

import { OrgType, OutcomeItemType, RubricType } from "components/server-types";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";

export const selectNestedOutcomeGroups = createAppSelector(
  [selectOutcomeGroups, selectOutcomeSubgroups, selectOutcomes],
  (outcomeGroups, outcomeSubgroups, outcomes) => {
    return outcomeGroups.map((outcomeGroup) => {
      let outcomeCount = 0;

      const filteredOutcomes = outcomes.filter(
        (outcome) => outcome.outcome_group_id === outcomeGroup.id,
      );
      outcomeCount += filteredOutcomes.length;

      const filteredOutcomeSubgroups = outcomeSubgroups
        .filter((outcomeSubgroup) => outcomeSubgroup.outcome_group_id === outcomeGroup.id)
        .map<NestedOutcomeSubgroupType>((outcomeSubgroup) => {
          const filteredSubgroupOutcomes = outcomes.filter(
            (outcome) => outcome.outcome_subgroup_id === outcomeSubgroup.id,
          );

          outcomeCount += filteredSubgroupOutcomes.length;

          return {
            ...outcomeSubgroup,
            outcomes: filteredSubgroupOutcomes,
          };
        });

      return {
        ...outcomeGroup,
        outcomeSubgroups: filteredOutcomeSubgroups,
        outcomes: filteredOutcomes,
        outcomeCount,
      } as NestedOutcomeGroupType;
    });
  },
);

export const selectCourseOutcomesForCourse = createAppSelector(
  [(state, courseId: string) => courseId, selectCourseOutcomes],
  (courseId, course_outcomes) =>
    course_outcomes.filter((course_outcome) => course_outcome.section_id === courseId),
);

export const selectFullCourseOutcomes = createAppSelector(
  [(state, courseId: string | null) => courseId, selectCourseOutcomes, (state) => state.outcomes],
  (courseId, course_outcomes, outcomes) => {
    const nestedCourseOutcomes = course_outcomes
      .filter((course_outcome) => course_outcome.section_id === courseId)
      .reduce((acc, course_outcome) => {
        const outcome = outcomes[course_outcome.outcome_id];
        if (outcome) {
          acc.push({
            ...course_outcome,
            outcome,
          } as NestedSectionOutcomeType);
        }
        return acc;
      }, [] as NestedSectionOutcomeType[]);
    return sortBy(nestedCourseOutcomes, (co) => co.outcome.title.toLowerCase());
  },
);

export const selectOrgOutcomesForCourse = createAppSelector(
  [selectFullCourseOutcomes, selectOutcomes],
  (courseOutcomes) => {
    const filteredCourseOutcomes = courseOutcomes.filter(
      (courseOutcome) => !!courseOutcome.outcome,
    );
    return filteredCourseOutcomes.map((courseOutcome) => courseOutcome.outcome);
  },
);

export const selectNestedPageOutcomes = createAppSelector(
  [
    (state, pageId: string) => pageId,
    selectPageOutcomes,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
  ],
  (pageId, page_outcomes, course_outcomes, outcomes) => {
    const nestedPageOutcomes = page_outcomes
      .filter((page_outcome) => page_outcome.page_id === pageId)
      .reduce((acc, page_outcome) => {
        const courseOutcome = course_outcomes[page_outcome.section_outcome_id];
        const outcome = outcomes[courseOutcome?.outcome_id];
        if (courseOutcome && outcome) {
          acc.push({
            ...page_outcome,
            outcome,
            course_outcome: courseOutcome,
          } as NestedPageOutcomeType);
        }
        return acc;
      }, [] as NestedPageOutcomeType[]);
    return sortBy(nestedPageOutcomes, (po) => po.outcome.title.toLowerCase());
  },
);

export const selectOrgOutcomeRubric = createAppSelector(
  [
    (state, outcomeId: string | undefined) => outcomeId,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (outcomeId, outcomes, org) => {
    if (!outcomeId || !org) return null;
    const outcome = outcomes[outcomeId];
    if (!outcome) return null;
    return determineRubric(org, [outcome]);
  },
);

export const selectCourseOutcomeRubric = createAppSelector(
  [
    (state, courseOutcomeId: string) => courseOutcomeId,
    (state) => state.section_outcomes,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (courseOutcomeId, course_outcomes, outcomes, org) => {
    const course_outcome = course_outcomes[courseOutcomeId];
    const outcome = outcomes[course_outcome?.outcome_id];
    if (!org || !course_outcome || !outcome) return null;
    return determineRubric(org, [course_outcome, outcome]);
  },
);

export const selectPageOutcomeRubric = createAppSelector(
  [
    (state, pageOutcomeId: string) => state.page_outcomes[pageOutcomeId],
    (state) => state.section_outcomes,
    (state) => state.outcomes,
    selectCurrentOrg,
  ],
  (page_outcome, course_outcomes, outcomes, org) => {
    const course_outcome = course_outcomes[page_outcome?.section_outcome_id];
    const outcome = outcomes[course_outcome?.outcome_id];
    if (!org || !page_outcome || !course_outcome || !outcome) return null;
    return determineRubric(org, [page_outcome, course_outcome, outcome]);
  },
);

const determineRubric = (org: OrgType, outcomeItems: OutcomeItemType[]): RubricType => {
  // Create a new rubric object by copying the default rubric for the org, then
  // potentially substituting rubric descriptions from the first supplied OutcomeItem
  // argument that has customized rubric descriptions, if any.
  const outcomeItem = outcomeItems.find((item) => !!item.rubric_descriptions);
  const customDescriptions = outcomeItem && outcomeItem.rubric_descriptions;
  return org.rubric.map((rubricLevel, index) => ({
    ...rubricLevel,
    description: customDescriptions ? customDescriptions[index] : rubricLevel.description,
  }));
};

export const selectPageOutcomesByPageId = createAppSelector(
  [selectPageOutcomes, (state, pageId: string) => pageId],
  (pageOutcomes, pageId) => pageOutcomes.filter((po) => po.page_id === pageId),
);

export const selectOutcomesForCurrentUser = createAppSelector(
  [selectCoursesForCurrentUser, selectCourseOutcomes, (state) => state.outcomes],
  (courses, courseOutcomes, outcomeMap) => {
    const courseIdsForUser = new Set(courses.map((course) => course.id));
    const courseOutcomesForUser = courseOutcomes.filter((courseOutcome) =>
      courseIdsForUser.has(courseOutcome.section_id),
    );
    // We need to get the outcomes from the course outcomes, but we could get duplicates.
    // We can use a Set to ensure that we only include each outcome ID once.
    const outcomeIdsForUser = new Set(
      courseOutcomesForUser.map((courseOutcome) => courseOutcome.outcome_id),
    );
    return Array.from(outcomeIdsForUser)
      .map((outcomeId) => outcomeMap[outcomeId])
      .filter(Boolean)
      .sort(ORDER_BY_TITLE);
  },
);
